import React, { useState, useEffect } from "react";
import {
  Table,
  Spinner,
  Pagination,
  FormControl,
  Button,
} from "react-bootstrap";
import uuid from "react-uuid";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import "./datasets.scss";
import Dashboard from "../../layout/Dashboard/Dashboard";
import line from "../../assets/images/line.png";
import CookieManager from "../../components/Cookies/CookieManager";
import "../../i18n";
import { useDatasets } from "../../hooks/useDatasets";
import "./datasets.scss";

const DemoMasterMetaData = () => {
  const { t } = useTranslation();
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [theadList, setTheadList] = useState([]);
  const [tbodyList, setTbodyList] = useState([]);
  const [sortConfig, setSortConfig] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;
  const { getSpecificDataset } = useDatasets(); // Hook to fetch dataset by ID

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const metadataId = queryParams.get("id");

  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        setIsTableLoading(true);
        const data = await getSpecificDataset(metadataId);
        const keys = Object.keys(data.elements[0]);
        setTheadList(keys);
        const values = data.elements.map((item) => Object.values(item));
        setTbodyList(values);
        setIsTableLoading(false);
      } catch (error) {
        console.error("Failed to fetch dataset:", error);
        setIsTableLoading(false);
      }
    };
    fetchMetaData();
  }, [metadataId]);

  const handleSort = (columnIndex) => {
    let direction = "ascending";
    if (
      sortConfig &&
      sortConfig.key === columnIndex &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key: columnIndex, direction });

    const sortedData = [...tbodyList].sort((a, b) => {
      const aVal = a[columnIndex];
      const bVal = b[columnIndex];

      if (aVal > bVal) return direction === "ascending" ? 1 : -1;
      if (aVal < bVal) return direction === "ascending" ? -1 : 1;
      return 0;
    });

    setTbodyList(sortedData);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredData = tbodyList.filter((row) =>
    row.some((value) =>
      value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const downloadCSV = () => {
    const csvRows = [];
    // Add the headers
    csvRows.push(theadList.map((header) => `"${header}"`).join(","));
    // Add the data rows
    tbodyList.forEach((row) => {
      csvRows.push(row.map((value) => `"${value}"`).join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "table_data.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <CookieManager />
      <Dashboard>
        <div>
          <h2 className="datasetsHeader">{t("metaDataHeader")}</h2>
          <p className="datasetsDesc">{t("metaDataDescription")}</p>
          <img className="separator" src={line} alt="" />

          {isTableLoading ? (
            <Spinner animation="border" variant="primary" />
          ) : (
            <div className="datasetsTable">
              <div className="d-flex justify-content-between mb-4">
                <FormControl
                  type="text"
                  placeholder="Search"
                  className="searchInput"
                  value={searchQuery}
                  onChange={handleSearch}
                />
                <Button
                  variant="success"
                  className="btn btn-success fw-bold"
                  onClick={downloadCSV}
                >
                  Download Table
                </Button>
              </div>

              <Table striped bordered hover>
                <thead>
                  <tr>
                    {theadList.map((item, index) => (
                      <th key={uuid()} onClick={() => handleSort(index)}>
                        {item}{" "}
                        {sortConfig && sortConfig.key === index
                          ? sortConfig.direction === "ascending"
                            ? "▲"
                            : "▼"
                          : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((rowData) => (
                    <tr key={uuid()}>
                      {rowData.map((value) => (
                        <td key={uuid()}>{value}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pagination>
                {[...Array(Math.ceil(filteredData.length / itemsPerPage))].map(
                  (_, index) => (
                    <Pagination.Item
                      key={index + 1}
                      active={index + 1 === currentPage}
                      onClick={() => paginate(index + 1)}
                    >
                      {index + 1}
                    </Pagination.Item>
                  )
                )}
              </Pagination>
            </div>
          )}
        </div>
      </Dashboard>
    </>
  );
};

export default DemoMasterMetaData;
